import { unitMessages } from './unit-messages';
import { DIMENSION_UNIT_CENTIMETERS } from '../types/dimensions';

import type { Dimensions, DimensionUnit } from '../types/dimensions';
import type { IntlShape } from 'react-intl';

const INCHES_PER_CM = 1 / 2.54;
const CM_PER_INCH = 2.54;

/** Abbreviations for display (e.g. "in", "cm") */
export const DIMENSION_UNIT_ABBREVIATIONS = { Inches: 'in', Centimeters: 'cm' } as const;

/** Format dimensions as "L x W x H unit" */
export const formatDimensions = (dimensions: Dimensions, intl: IntlShape): string => {
  const abbrev =
    dimensions.unit === DIMENSION_UNIT_CENTIMETERS ? intl.formatMessage(unitMessages.centimeters) : intl.formatMessage(unitMessages.inches);
  return `${dimensions.length} x ${dimensions.width} x ${dimensions.height} ${abbrev}`;
};

/**
 * Convert dimensions from one unit to another.
 * Returns the original dimensions unchanged when fromUnit === toUnit.
 */
export const convertDimensions = (dimensions: Dimensions, toUnit: DimensionUnit): Dimensions => {
  if (dimensions.unit === toUnit) return dimensions;

  const factor = toUnit === DIMENSION_UNIT_CENTIMETERS ? CM_PER_INCH : INCHES_PER_CM;
  const round = (n: number) => Math.round(n * 100) / 100;

  return {
    length: round(dimensions.length * factor),
    width: round(dimensions.width * factor),
    height: round(dimensions.height * factor),
    unit: toUnit,
  };
};

/** Lowercase label for a dimension unit (e.g. "inches", "centimeters") */
export const getDimensionUnitLabel = (unit: DimensionUnit): string => {
  return unit === 'Inches' ? 'inches' : 'centimeters';
};

export const hasAnyDimensionValues = (dimensions: Dimensions | undefined | null): boolean => {
  return Boolean(dimensions && (dimensions.length > 0 || dimensions.width > 0 || dimensions.height > 0));
};
